/**
 * AudioEngine.ts — the WebAudio core: one lazily-created context (browsers
 * only allow audio after a user gesture), a master bus split into SFX + music,
 * and volume plumbing from settings. Everything the game plays is SYNTHESIZED
 * — zero audio assets, zero licensing, near-zero download (owner-approved
 * 2026-06-11). In test/headless environments every call is a safe no-op.
 */
export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  sfxBus: GainNode | null = null;
  musicBus: GainNode | null = null;

  private volumes = { master: 1, sfx: 1, music: 0.8 };
  /** Temporary music dip (fanfares talk over the soundtrack). */
  private duck = 1;
  /** M88 — hard mute (the title speaker button): master drops to 0 while the
   *  volume SETTINGS keep their values, so unmuting restores exactly. */
  private muted = false;

  /** Fires whenever the context enters 'running' (M85b): immediately in
   *  autoplay-allowed environments (installed PWA, the Capacitor shell,
   *  returning players Chrome trusts with sound), otherwise on the first
   *  gesture. Game listens and announces `audioUnlocked` exactly once. */
  onRunning: (() => void) | null = null;

  /** The context, if audio is possible and unlocked; null otherwise. */
  get context(): AudioContext | null {
    return this.ctx;
  }

  /** Create/resume the context. Called from every gesture (pointerdown /
   *  keydown) AND once at page load (M85b, the title-music autostart): where
   *  the platform allows autoplay the context starts running right away; where
   *  it doesn't, the queued resume() below makes Chrome auto-resume on the
   *  user's FIRST interaction of any kind (that's documented autoplay-policy
   *  behavior, and `statechange` tells us the moment it happens). */
  unlock(): void {
    if (typeof window === 'undefined' || typeof AudioContext === 'undefined') return;
    if (!this.ctx) {
      this.ctx = new AudioContext();
      this.master = this.ctx.createGain();
      this.master.connect(this.ctx.destination);
      this.sfxBus = this.ctx.createGain();
      this.sfxBus.connect(this.master);
      this.musicBus = this.ctx.createGain();
      this.musicBus.connect(this.master);
      this.applyVolumes();
      this.ctx.addEventListener('statechange', () => {
        if (this.ctx?.state === 'running') this.onRunning?.();
      });
      if (this.ctx.state === 'running') {
        // Autoplay allowed — no statechange will fire; announce on the next
        // microtask so load-time callers finish wiring their listeners first.
        queueMicrotask(() => this.onRunning?.());
      }
    }
    // Safari rejects a no-gesture resume with NotAllowedError — swallow it
    // (the next real gesture calls unlock() again).
    if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => {});
  }

  setVolumes(master: number, sfx: number, music: number): void {
    this.volumes = { master, sfx, music };
    this.applyVolumes();
  }

  /** M88 — mute/unmute EVERYTHING (sfx + synth music + the real-file decks:
   *  they all route through the one master bus). */
  setMuted(muted: boolean): void {
    this.muted = muted;
    this.applyVolumes();
  }

  /** Dip the music (0..1 = fraction of normal) — used under fanfares. */
  setDuck(duck: number): void {
    this.duck = duck;
    this.applyVolumes();
  }

  private applyVolumes(): void {
    if (!this.ctx || !this.master || !this.sfxBus || !this.musicBus) return;
    const t = this.ctx.currentTime;
    this.master.gain.setTargetAtTime(this.muted ? 0 : this.volumes.master, t, 0.05);
    this.sfxBus.gain.setTargetAtTime(this.volumes.sfx, t, 0.05);
    this.musicBus.gain.setTargetAtTime(this.volumes.music * 0.7 * this.duck, t, 0.12);
  }
}

/** The game-wide audio singleton (no-op until unlock() on the first gesture). */
export const audioEngine = new AudioEngine();
